import { MyLocationRounded } from '@mui/icons-material'
import BiotechIcon from '@mui/icons-material/Biotech'
import CompareIcon from '@mui/icons-material/Compare'
import DiamondIcon from '@mui/icons-material/Diamond'
import ErrorIcon from '@mui/icons-material/Error'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Box, Button, Card, CardContent, CardHeader, IconButton, List, ListItem, Menu, MenuItem, Modal, Popover, Tooltip, Typography, useTheme } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { useReactFlow } from '@xyflow/react'
import { useEffect, useState } from 'react'
import algo from '../../../core/Algo'
import { IApprovalRequest } from '../../../definitions/Approval'
import { IFlow } from '../../../definitions/Nodes'
import { IExecutionEnvironment, IFlowRunConfig, ISimpleExecutionEnvironment } from '../../../definitions/NodesExecution'
import EventsEngine from '../../../engines/EventsEngine'
import Formatter from '../../../helpers/Formatter'
import Helper from '../../../helpers/Helper'
import FlowExecutionManager from '../../../managers/FlowExecutionManager'
import { IFlowChange } from '../../../managers/FlowUIManager'
import ValidationManager from '../../../managers/ValidationManager'
import useInvalidate from '../../hooks/useInvalidate'
import { buildQueryKey } from '../../hooks/useQuery'
import ListExeEnvsModal from '../ListExeEnvsModal'
import { FlowEditorMode } from '../SettlementFlowEditor'
import StartRunModal from '../StartRunModal'
import ModalEndExecutionEnvironment from './ModalEndExecutionEnvironment'

interface P {
    flow: IFlow
    setFlow: (flow: IFlow) => void
    editorMode: FlowEditorMode
    comparedExecEnv: ISimpleExecutionEnvironment
    execEnv: IExecutionEnvironment
    runMode: 'dry' | 'full'
    color?: any
    setRunMode: (runMode: 'dry' | 'full') => void
    setEE: (execEnv: IExecutionEnvironment) => void
    setEditorMode: (editorMode: FlowEditorMode) => void
    setComparedExecEnv: (comparedExecEnv: IExecutionEnvironment) => void
    applyFlowChange: (change: IFlowChange) => void
    flowRunConfig?: IFlowRunConfig
    approval: IApprovalRequest
}

const ExecuteFlowButton = ({ editorMode, flow, setFlow, runMode, color, execEnv: localEE, setEE, setRunMode, setEditorMode, setComparedExecEnv, flowRunConfig, approval }: P) => {
    const theme = useTheme()
    const [openErrorModal, setOpenErrorModal] = useState(false)
    const [openCompletedModal, setOpenCompletedModal] = useState(false)
    const [anchorElMenu, setAnchorElMenu] = useState<HTMLElement>(null)
    const [executionTime, setExecutionTime] = useState(0)
    const [openStartRunModal, setOpenStartRunModal] = useState(false)
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>(null)
    const validationErrors = {
        errors: [...ValidationManager.validateFlow(flow).errors, ...ValidationManager.validateNodes(flow.nodes).errors],
        nodesId: [...ValidationManager.validateFlow(flow).nodesId, ...ValidationManager.validateNodes(flow.nodes).nodesId]
    }
    const hasErrors = validationErrors.errors.length > 0
    const isSubFlow = flow.type === 'sub-flow'
    const pixelRadius = 'calc(var(--unit)*3)'
    const flowInstance = useReactFlow()
    const isCompiled = flow.nodes.every(n => n?.jsonSchema)
    const isOldLive = approval.status === 'approved' && !flow.versioning.isHead
    const isLive = approval.status === 'approved' && flow.versioning.isHead
    const invalidateReports = useInvalidate({ queryKey: buildQueryKey('compiledReports-/*') })
    const invalidateFlows = useInvalidate({ queryKey: buildQueryKey('settlements/*') })

    useEffect(() => {
        if (editorMode === 'edit') {
            setExecutionTime(0)
            return
        }
        if (editorMode === 'audit') return
        const intervalId = setInterval(() => {
            setExecutionTime(executionTime + 1)
        }, 1000)
        return () => clearInterval(intervalId)
    }, [editorMode, executionTime])

    const onFinishPolling = async (execEnv: IExecutionEnvironment) => {
        setEE(execEnv)
    }

    const onFinishAnimation = async () => {
        setEditorMode('audit')
        EventsEngine.publish('disable-switch-tab-flow', false)
        setOpenCompletedModal(true)
    }

    const onStart = async (flowRunConfig: IFlowRunConfig) => {
        if (isOldLive) return
        invalidateReports()
        invalidateFlows()

        setEditorMode(flowRunConfig.dryRunConfig ? 'dry-run' : 'run')
        EventsEngine.publish('disable-switch-tab-flow', true)
        let execEnv: IExecutionEnvironment = null

        if (algo.hasVal(flowRunConfig.dryRunConfig))
            execEnv = await FlowExecutionManager.startFlowDryRunExecution(onFinishPolling, onFinishAnimation, flowRunConfig)
        else
            execEnv = await FlowExecutionManager.startFlowExecution(onFinishPolling, onFinishAnimation, flowRunConfig)

        if (Helper.hasVal(execEnv))
            setFlow(execEnv.flow)

        setEE(execEnv)
    }

    useEffect(() => {
        if (flowRunConfig) {
            const call = async () => await onStart(flowRunConfig)
            call()
        }
    }, [])

    const onStop = async () => {
        setEditorMode('edit')
        EventsEngine.publish('disable-switch-tab-flow', false)
        await FlowExecutionManager.stopFlowExecution(flow)
        setEE(null)
    }

    const renderButton = () => {
        if (hasErrors) {
            return (
                <Box
                    sx={{ cursor: 'pointer', display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center', padding: 1, backgroundColor: 'var(--color-bad)', borderRadius: `0 ${pixelRadius} ${pixelRadius} 0` }}
                    onClick={() => setOpenErrorModal(true)}>
                    <ErrorIcon fontSize='small' style={{ color: 'white' }} />
                    <Typography color='white' variant='body2' noWrap>
                        {`${validationErrors.errors.length} ${validationErrors.errors.length === 1 ? 'issue' : 'issues'}`}
                    </Typography>
                </Box>

            )
        } else {
            switch (editorMode) {
                case 'edit': {
                    return (
                        <div style={{ position: 'relative' }}>
                            <Tooltip title={!isCompiled ? 'You need to compile first the flow' : runMode === 'full' ? 'Start flow execution' : 'Start dry-run execution'} arrow>
                                <div>
                                    <Button
                                        variant='contained'
                                        disabled={isOldLive || isSubFlow || !isCompiled}
                                        color={color}
                                        sx={{ paddingRight: 4 }}
                                        onClick={() => (runMode === 'full' || runMode === 'dry') && setOpenStartRunModal(true)}>
                                        <Typography color={runMode === 'dry' && 'dryRun'}>
                                            {runMode === 'full' ? 'Start' : 'Test'}
                                        </Typography>
                                    </Button>
                                </div>
                            </Tooltip>
                            {isLive && (
                                <div style={{ position: 'absolute', top: 0, right: 0, width: 'calc(var(--unit) * 4)', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: 999 }}>
                                    <IconButton onClick={e => setAnchorElMenu(e.currentTarget)} disabled={isSubFlow || !isCompiled} style={{ width: '100%', height: '100%' }}>
                                        <KeyboardArrowUpIcon
                                            sx={{ color: theme.palette.primary.contrastText }}
                                            style={{
                                                transition: 'transform 0.3s ease',
                                                transform: Helper.hasVal(anchorElMenu) ? 'rotate(180deg)' : 'rotate(0deg)'
                                            }}
                                        />
                                    </IconButton>
                                    <Menu
                                        disableAutoFocus
                                        id='basic-menu'
                                        variant='menu'
                                        sx={{ top: 'calc(-1 * var(--unit) * 6)', left: 'calc(-1 * var(--unit) * 7)' }}
                                        anchorEl={anchorElMenu}
                                        open={Helper.hasVal(anchorElMenu)}
                                        onClose={() => setAnchorElMenu(null)}
                                        MenuListProps={{
                                            'aria-labelledby': 'basic-button'
                                        }}>
                                        <MenuItem
                                            sx={{
                                                'borderRadius': 'calc(var(--unit) * 2)',
                                                '&:hover': { backgroundColor: Helper.hexToRgbA(theme.palette['dryRun'].main, 0.1) }
                                            }}
                                            onClick={() => {
                                                setRunMode('dry')
                                                setAnchorElMenu(null)
                                            }}>
                                            <BiotechIcon sx={{ marginRight: 1 }} />
                                            <Typography variant='button'>Test Flow</Typography>
                                        </MenuItem>
                                        <MenuItem
                                            sx={{
                                                'borderRadius': 'calc(var(--unit) * 2)',
                                                '&:hover': { backgroundColor: Helper.hexToRgbA(theme.palette['primary'].main, 0.1) }
                                            }}
                                            onClick={() => {
                                                setRunMode('full')
                                                setAnchorElMenu(null)
                                            }}>
                                            <DiamondIcon sx={{ marginRight: 1 }} />
                                            <Typography variant='button'>Start Flow</Typography>
                                        </MenuItem>
                                    </Menu>
                                </div>
                            )}
                        </div>
                    )
                }
                case 'dry-run':
                case 'run': {
                    return (
                        <div>
                            <Button variant='contained' color={color} onClick={() => onStop()}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                                    <CircularProgress size={17} sx={{ color: 'var(--bg-color)' }} />
                                    <Typography>
                                        Stop
                                    </Typography>
                                    <Typography>
                                        {Formatter.timer(executionTime, 'minutes')}
                                    </Typography>
                                </Box>
                            </Button>
                        </div>
                    )
                }
                case 'audit': {
                    return (
                        <div>
                            <Button
                                variant='contained'
                                color={color}
                                onClick={e => {
                                    if (algo.hasVal(anchorEl))
                                        setAnchorEl(null)
                                    else
                                        setAnchorEl(e.currentTarget)
                                }}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                                    <CompareIcon fontSize='small' />
                                    <Typography>
                                        Compare
                                    </Typography>
                                </Box>
                            </Button>
                        </div>
                    )
                }
                case 'compare': {
                    return (
                        <div>
                            <Button
                                variant='contained'
                                color={color}
                                onClick={e => {
                                    if (algo.hasVal(anchorEl))
                                        setAnchorEl(null)
                                    else
                                        setAnchorEl(e.currentTarget)
                                }}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
                                    <CompareIcon fontSize='small' />
                                    <Typography>
                                        Change comparison
                                    </Typography>
                                </Box>
                            </Button>
                        </div>
                    )
                }
            }
        }
    }

    return (
        <>
            {renderButton()}
            <Modal open={openErrorModal} onClose={() => setOpenErrorModal(false)}>
                <Box margin='80px auto auto' maxWidth='80vw' sx={{ padding: 'calc(var(--unit) * 2)' }}>
                    <Card>
                        <CardHeader title='Errors' />
                        <CardContent style={{ maxHeight: '75vh', overflowY: 'auto', gap: 10 }}>
                            <List>
                                {validationErrors.errors.map((error, index) => {
                                    const nodeId = validationErrors.nodesId[index]
                                    return (
                                        <ListItem key={index} divider sx={{ gap: 'calc(var(--unit) * 2)', display: 'flex', justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', gap: 'calc(var(--unit) * 2)', alignItems: 'center' }}>
                                                <Typography color={theme.palette.text.secondary} variant='body1'>{index + 1}</Typography>
                                                <Typography variant='body2'>{error}</Typography>
                                            </Box>
                                            <Box>
                                                <Tooltip title='Go to error' arrow>
                                                    <IconButton
                                                        disabled={!nodeId}
                                                        onClick={() => {
                                                            setOpenErrorModal(false)
                                                            flowInstance.fitView({ nodes: [{ id: nodeId }], duration: 1500 })
                                                        }}>
                                                        <MyLocationRounded />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </ListItem>
                                    )
                                })}
                            </List>
                        </CardContent>
                    </Card>
                </Box>
            </Modal>
            {openStartRunModal && (
                <Modal open={openStartRunModal} onClose={() => setOpenStartRunModal(false)} sx={{ display: 'flex', width: 'calc(100vw - var(--left-nav-width))', left: 'var(--left-nav-width)', top: '8vh' }}>
                    <div style={{ margin: 'auto auto' }}>
                        <StartRunModal flow={flow} onStart={onStart} setOpenStartRunModal={setOpenStartRunModal} runMode={runMode} />
                    </div>
                </Modal>
            )}
            <Box>
                <Popover
                    open={algo.hasVal(anchorEl)}
                    anchorEl={anchorEl}
                    disableEnforceFocus={true}
                    disableAutoFocus={true}
                    onClose={() => setAnchorEl(null)}
                    sx={{ top: 'calc(-1 * var(--unit) * 6)', right: 'calc(var(--left-nav-width))' }}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Box sx={{ width: '40vw', height: '50vh' }}>
                        <ListExeEnvsModal flow={flow} setEditorMode={setEditorMode} onClose={() => setAnchorEl(null)} setComparedExecEnv={setComparedExecEnv} />
                    </Box>
                </Popover>
            </Box>
            {localEE && (
                <Modal open={openCompletedModal} onClose={() => setOpenCompletedModal(false)} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', width: 'calc(100vw - var(--left-nav-width))', left: 'calc(var(--left-nav-width))' }}>
                    <div style={{ margin: '80px auto auto', width: 700 }}>
                        <ModalEndExecutionEnvironment execEnv={localEE} timer={executionTime} />
                    </div>
                </Modal>
            )}
        </>
    )
}

export default ExecuteFlowButton
